/**
 * Frontend
 * Input Sanitization Utilities
 * Provides comprehensive input sanitization for frontend
 */

import React from 'react';

// Remove emojis (all Unicode emoji ranges)
export const removeEmojis = (str) => {
  if (typeof str !== 'string') return str;
  
  return str.replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Misc Symbols and Pictographs
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport and Map
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // Flags
    .replace(/[\u{2600}-\u{26FF}]/gu, '') // Misc symbols
    .replace(/[\u{2700}-\u{27BF}]/gu, '') // Dingbats
    .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Supplemental Symbols and Pictographs
    .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '') // Chess Symbols
    .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Symbols and Pictographs Extended-A
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '') // Variation Selectors
    .replace(/[\u{200D}]/gu, '') // Zero Width Joiner
    .trim();
};

// Remove HTML tags and dangerous characters
export const stripHtml = (str) => {
  if (typeof str !== 'string') return str;
  
  return str
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    .replace(/<img[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

// Sanitize input for safe display
export const sanitizeInput = (str, options = {}) => {
  if (typeof str !== 'string') return str;
  
  const {
    allowHtml = false,
    allowEmojis = false,
    maxLength = 10000,
    allowSpecialChars = true
  } = options;
  
  let sanitized = str;
  
  // Remove emojis
  if (!allowEmojis) {
    sanitized = removeEmojis(sanitized);
  }
  
  // Remove HTML
  if (!allowHtml) {
    sanitized = stripHtml(sanitized);
  }
  
  // Remove control characters except newlines and tabs
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  // Remove special characters if not allowed
  if (!allowSpecialChars) {
    sanitized = sanitized.replace(/[^\w\s@.-]/g, '');
  }
  
  return sanitized.trim();
};

// React hook for sanitized input
export const useSanitizedInput = (initialValue = '', options = {}) => {
  const [value, setValue] = React.useState(sanitizeInput(initialValue, options));
  
  const handleChange = (e) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeInput(rawValue, options);
    setValue(sanitized);
    
    // Silent sanitization - no warnings to avoid revealing security measures
  };
  
  return [value, handleChange, setValue];
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// Validate phone format
export const isValidPhone = (phone) => {
  const phoneRegex = /^\+?[\d\s-()]{10,}$/;
  return phoneRegex.test(phone);
};

// Validate URL format
export const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

// Check if string contains emojis
export const containsEmojis = (str) => {
  if (typeof str !== 'string') return false;
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]/gu;
  return emojiRegex.test(str);
};

// Check if string contains HTML
export const containsHtml = (str) => {
  if (typeof str !== 'string') return false;
  return /<[^>]+>/.test(str);
};

// Sanitize on paste event
export const handleSanitizedPaste = (e, options = {}) => {
  e.preventDefault();
  const pastedText = e.clipboardData.getData('text/plain');
  const sanitized = sanitizeInput(pastedText, options);
  
  // Insert sanitized text at cursor position
  const target = e.target;
  const start = target.selectionStart;
  const end = target.selectionEnd;
  const currentValue = target.value;
  
  const newValue = currentValue.substring(0, start) + sanitized + currentValue.substring(end);
  target.value = newValue;
  
  // Set cursor position after inserted text
  target.selectionStart = target.selectionEnd = start + sanitized.length;
  
  // Trigger change event
  const event = new Event('input', { bubbles: true });
  target.dispatchEvent(event);
  
  // Silent sanitization - no warnings to avoid revealing security measures
};

// Export as named object to satisfy linter
const sanitizationUtils = {
  sanitizeInput,
  removeEmojis,
  stripHtml,
  useSanitizedInput,
  isValidEmail,
  isValidPhone,
  isValidUrl,
  containsEmojis,
  containsHtml,
  handleSanitizedPaste
};

export default sanitizationUtils;
